import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { API, useAuth } from "@/App";
import axios from "axios";
import { 
  Crown, 
  Check, 
  X, 
  Calendar,
  TrendingUp,
  Shield,
  CreditCard,
  Loader2,
  Sparkles,
  Users
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";

const VALUE_POINTS = [
  {
    icon: Calendar,
    text: "Track all income and bills in one place"
  },
  {
    icon: TrendingUp,
    text: "See your financial future clearly with calendar view"
  },
  {
    icon: Shield,
    text: "Stay in control and avoid missed payments"
  },
  {
    icon: CreditCard,
    text: "Reduce debt faster with visual tracking"
  }
];

export default function PaywallModal({ 
  isOpen, 
  onClose, 
  triggerSource = "manual",
  lockedFeature = null 
}) {
  const navigate = useNavigate();
  const { user, refreshUser } = useAuth();
  const [selectedPlan, setSelectedPlan] = useState('annual');
  const [loading, setLoading] = useState(false);
  const [trialStatus, setTrialStatus] = useState(null);

  useEffect(() => {
    if (isOpen) {
      fetchTrialStatus();
      // Track paywall view
      trackPaywallEvent('paywall_viewed', { trigger_source: triggerSource, locked_feature: lockedFeature });
    }
  }, [isOpen, triggerSource, lockedFeature]);

  const fetchTrialStatus = async () => {
    try {
      const response = await axios.get(`${API}/subscription/trial-status`, {
        withCredentials: true
      });
      setTrialStatus(response.data);
    } catch (error) {
      console.error("Error fetching trial status:", error);
    }
  };

  const trackPaywallEvent = async (eventName, metadata = {}) => {
    try {
      await axios.post(`${API}/events/track`, {
        event_name: eventName,
        metadata: {
          ...metadata,
          selected_plan: selectedPlan
        }
      }, { withCredentials: true });
    } catch (error) {
      console.error("Error tracking event:", error);
    }
  };

  const handleStartTrial = async () => {
    setLoading(true);
    
    try {
      // Track CTA click
      await trackPaywallEvent('paywall_cta_clicked', { 
        plan: selectedPlan,
        trigger_source: triggerSource 
      });

      const response = await axios.post(`${API}/subscription/create-checkout-session`, {
        plan: selectedPlan,
        origin_url: window.location.origin,
        include_trial: true
      }, { withCredentials: true });

      // Redirect to Stripe Checkout
      window.location.href = response.data.url;
    } catch (error) {
      console.error("Checkout error:", error);
      toast.error(error.response?.data?.detail || "Failed to start checkout");
      setLoading(false);
    }
  };

  if (!isOpen) return null;

  const isPremium = trialStatus?.is_premium;
  const isTrialing = trialStatus?.plan_type === 'trial' && !trialStatus?.trial_expired;

  // If already premium, don't show paywall
  if (isPremium && trialStatus?.subscription_status === 'active') {
    onClose?.();
    return null;
  }

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/60 backdrop-blur-sm">
      <div 
        className="relative w-full max-w-md bg-gradient-to-b from-slate-900 to-slate-950 rounded-2xl shadow-2xl border border-slate-700/50 overflow-hidden"
        data-testid="paywall-modal"
      >
        {/* Close button */}
        <button
          onClick={onClose}
          className="absolute top-4 right-4 p-2 rounded-full hover:bg-slate-800/50 text-slate-400 hover:text-white transition-colors z-10"
          data-testid="paywall-close-btn"
        >
          <X className="w-5 h-5" />
        </button>

        {/* Header with gradient accent */}
        <div className="relative pt-8 pb-6 px-6 text-center">
          <div className="absolute inset-0 bg-gradient-to-b from-emerald-500/10 to-transparent" />
          
          <div className="relative">
            <div className="w-14 h-14 mx-auto mb-4 bg-gradient-to-br from-emerald-400 to-emerald-600 rounded-2xl flex items-center justify-center shadow-lg shadow-emerald-500/25">
              <Crown className="w-7 h-7 text-white" />
            </div>
            
            <h2 className="text-2xl font-bold text-white mb-2">
              Take Control of Your Money
            </h2>
            <p className="text-slate-400 text-sm">
              Unlock full clarity, stay organised, and eliminate financial stress
            </p>
          </div>
        </div>

        {/* Value Points */}
        <div className="px-6 pb-4">
          <div className="space-y-3">
            {VALUE_POINTS.map((point, index) => (
              <div key={index} className="flex items-center gap-3">
                <div className="w-8 h-8 rounded-lg bg-emerald-500/10 flex items-center justify-center flex-shrink-0">
                  <point.icon className="w-4 h-4 text-emerald-400" />
                </div>
                <span className="text-sm text-slate-300">{point.text}</span>
              </div>
            ))}
          </div>
        </div>

        {/* Pricing Cards */}
        <div className="px-6 py-4">
          <div className="grid grid-cols-2 gap-3">
            {/* Monthly Plan */}
            <div 
              className={`relative p-4 rounded-xl cursor-pointer transition-all duration-200 border-2 ${
                selectedPlan === 'monthly' 
                  ? 'bg-slate-800/80 border-slate-500 shadow-lg' 
                  : 'bg-slate-800/30 border-slate-700/50 hover:border-slate-600'
              }`}
              onClick={() => setSelectedPlan('monthly')}
              data-testid="paywall-plan-monthly"
            >
              <p className="text-xs text-slate-500 uppercase tracking-wide mb-1">Flexible</p>
              <p className="text-sm text-slate-400 mb-2">Monthly</p>
              <div className="flex items-baseline gap-1">
                <span className="text-2xl font-bold text-white">£4.99</span>
                <span className="text-slate-500 text-sm">/mo</span>
              </div>
            </div>
            
            {/* Annual Plan - Highlighted */}
            <div 
              className={`relative p-4 rounded-xl cursor-pointer transition-all duration-200 border-2 ${
                selectedPlan === 'annual' 
                  ? 'bg-emerald-500/15 border-emerald-500 shadow-lg shadow-emerald-500/10' 
                  : 'bg-emerald-500/5 border-emerald-500/30 hover:border-emerald-500/50'
              }`}
              onClick={() => setSelectedPlan('annual')}
              data-testid="paywall-plan-annual"
            >
              {/* Best Value Badge */}
              <div className="absolute -top-2.5 left-1/2 -translate-x-1/2 px-2.5 py-0.5 bg-emerald-500 text-white text-xs font-semibold rounded-full shadow-sm">
                Best Value
              </div>
              
              <p className="text-xs text-emerald-400/80 uppercase tracking-wide mb-1 mt-1">Save £19.89</p>
              <p className="text-sm text-slate-400 mb-2">Annual</p>
              <div className="flex items-baseline gap-1">
                <span className="text-2xl font-bold text-white">£39.99</span>
                <span className="text-slate-500 text-sm">/yr</span>
              </div>
              <p className="text-xs text-emerald-400 mt-1">Just £3.33/month</p>
            </div>
          </div>
        </div>

        {/* CTA Section */}
        <div className="px-6 pb-4">
          <Button
            onClick={handleStartTrial}
            disabled={loading}
            className="w-full h-12 bg-gradient-to-r from-emerald-500 to-emerald-600 hover:from-emerald-600 hover:to-emerald-700 text-white font-semibold text-base rounded-xl shadow-lg shadow-emerald-500/25 transition-all duration-200"
            data-testid="paywall-cta-btn"
          >
            {loading ? (
              <>
                <Loader2 className="w-5 h-5 animate-spin mr-2" />
                Opening checkout...
              </>
            ) : (
              <>
                <Sparkles className="w-5 h-5 mr-2" />
                Start Free Trial
              </>
            )}
          </Button>
          
          <p className="text-center text-xs text-slate-500 mt-2">
            Card required • Cancel anytime before trial ends
          </p>
        </div>

        {/* Trust Section */}
        <div className="px-6 pb-6">
          <div className="flex items-center justify-center gap-2 py-3 bg-slate-800/30 rounded-xl">
            <Shield className="w-4 h-4 text-slate-500" />
            <span className="text-xs text-slate-500">Card required • Cancel anytime before trial ends</span>
          </div>
        </div>

        {/* Social Proof */}
        <div className="px-6 pb-6">
          <div className="flex items-center justify-center gap-2 text-slate-500">
            <Users className="w-4 h-4" />
            <span className="text-xs">Join early users improving their finances</span>
          </div>
        </div>
      </div>
    </div>
  );
}
